<template>
  <q-dialog
    ref="dialog"
    :persistent="linkPromptMode"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin appointments-dialog">
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ title }}
        </div>
        <div class="row items-center q-col-gutter-sm">
          <div
            v-if="showRefreshAppointmentsButton"
          >
            <q-btn
              icon="refresh"
              flat
              round
              dense
              :disable="offlineMode"
              :loading="isRefreshingAppointments"
              @click="onRefreshAppointments"
            />
          </div>
          <div v-if="!linkPromptMode">
            <q-btn
              v-close-popup
              flat
              round
              dense
              icon="close"
              class="col-auto"
            />
          </div>
        </div>
      </q-card-section>
      <q-card-section
        v-if="linkPromptMode"
        class="q-pt-none"
      >
        {{ $t('sessions.modals.changeSessionAppointment.endOfSessionSubtitle') }}
      </q-card-section>
      <q-card-section class="row">
        <simple-appointments-table
          v-model:selected="selectedAppointmentArr"
          :appointments="appointments"
          :pagination="{
            sortBy: 'scheduledStartTimestamp',
            descending: false,
            rowsPerPage: 50,
          }"
          flat
          row-key="id"
          selection="single"
          :loading="isRefreshingAppointments"
          :no-data-label="linkPromptMode
            ? $t('sessions.modals.changeSessionAppointment.emptyStateNoAppointments')
            : null"
          class="full-width"
          @row-click="onSelectRow"
        />
      </q-card-section>
      <q-card-actions class="row">
        <q-btn
          v-if="!!appointmentId && allowUnlink"
          color="negative"
          :label="$t('run.overview.appointmentUnlink')"
          flat
          @click="onUnlinkClick"
        />
        <q-space />
        <q-btn
          color="secondary"
          :label="cancelLabel"
          flat
          @click="onCancelClick"
        />
        <q-btn
          color="primary"
          :loading="showSpinner"
          :label="okLabel"
          :disable="!canSubmit"
          flat
          data-e2e="button-save"
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import SimpleAppointmentsTable from 'components/Appointments/SimpleAppointmentsTable';
import { Timestamp } from 'firebase/firestore';
import { chain, filter, find, includes, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import { useAppointments } from 'src/composables/appointments';
import { POLLING_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import AppointmentsDB from 'src/fb/appointments-db';
import ClientsDB from 'src/fb/clients-db';
import UsersDB from 'src/fb/users-db';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import { notifyErrorWithDetails } from 'src/utils/AppUtils';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { useTime } from 'src/composables/time';

export default {
  name: 'ChangeSessionActivityAppointment',

  components: { SimpleAppointmentsTable },

  mixins: [Integrations, Interactions],

  props: {

    appointmentId: {
      type: String,
      required: true,
    },

    clientId: {
      type: String,
      default: null,
    },

    userId: {
      type: String,
      default: null,
    },

    startDate: {
      type: Date,
      required: true,
    },

    allowUnlink: {
      type: Boolean,
      default: false,
    },

    disableAppointments: {
      type: Array,
      default: () => ([]),
    },

    linkPromptMode: {
      type: Boolean,
      default: false,
    },

    // when provided, the modal shows these instead of querying on open
    // (used by the end-of-session flow, which has already fetched them to decide whether to open)
    preloadedAppointments: {
      type: Array,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { filterAppointmentsWithCorrectUser } = useAppointments();
    const { adjustDate } = useTime();

    return { filterAppointmentsWithCorrectUser, adjustDate };
  },

  data() {
    const startTimestamp = Timestamp.fromDate(
      this.adjustDate(this.startDate, { hour: 0, minute: 0, second: 0, millisecond: 0 }),
    );
    return {
      showSpinner: false,
      isRefreshingAppointments: false,

      selectedAppointmentId: null,
      startTimestamp,
      appointments: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'connectedClinic']),
    ...mapState(network, ['offlineMode', 'networkOnline']),

    canSubmit() {
      return this.selectedAppointmentId && (this.selectedAppointmentId !== this.appointmentId);
    }, // canSubmit()

    selectedAppointmentArr: {
      get() {
        const selection = find(this.appointments, { id: this.selectedAppointmentId });
        if ((this.selectedAppointmentId === null) || !selection) {
          return [];
        }
        return [selection];
      },
      set(appointmentIds) {
        if (isEmpty(appointmentIds)) {
          this.selectedAppointmentId = null;
        } else {
          this.selectedAppointmentId = chain(appointmentIds).head().get('id').value();
        }
      },
    },

    showRefreshAppointmentsButton() {
      return (this.connectedClinic.integration !== null)
        && (POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration));
    }, // pollingDrivenIntegrations

    title() {
      return this.linkPromptMode
        ? this.$t('sessions.modals.changeSessionAppointment.endOfSessionTitle')
        : this.$t('sessions.modals.changeSessionAppointment.textTitle');
    },

    cancelLabel() {
      return this.linkPromptMode
        ? this.$t('sessions.modals.changeSessionAppointment.buttonSkip')
        : this.$t('common.buttonCancel');
    },

    okLabel() {
      return this.linkPromptMode
        ? this.$t('sessions.modals.changeSessionAppointment.buttonLink')
        : this.$t('common.buttonSave');
    },

  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.selectedAppointmentId = this.appointmentId;
      if (this.preloadedAppointments) {
        this.appointments = this.preloadedAppointments;
      } else {
        this.loadAppointments();
      }

      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onOKClick() {
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      this.$emit('ok', this.selectedAppointmentId);
      // or with payload: this.$emit('ok', { ... })

      // then hiding dialog
      this.hide();
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onUnlinkClick() {
      const confirmMessage = this.connectedClinic.integration
        ? this.$t('run.overview.confirmUnlinkAppointment', {
          integrationLabel: this.getIntegrationName(this.connectedClinic.integration),
        })
        : this.$t('run.overview.confirmUnlinkAppointmentNoIntegration');
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        confirmMessage,
        () => {
          this.$emit('ok', null);
          this.hide();
        },
      );
    },

    onSelectRow(evt, row) {
      this.selectedAppointmentArr = [row];
    },

    async onRefreshAppointments() {
      this.isRefreshingAppointments = true;
      try {
        const isStaffActivity = (this.currentClient?.id === this.clientId) && (this.currentClient?.type === 'staff');
        const isClient = !!this.clientId && !isStaffActivity;
        let objectType = isClient ? 'client' : 'staff';
        const integrationReferencePerType = {
          [this.currentClient?.id]: this.currentClient?.integrationReference,
          [this.userDetails?.userId]: this.userDetails?.integrationReference,
        };
        const id = isClient ? this.clientId : this.userId;

        let objectId = integrationReferencePerType[id] || null;
        if (!Object.keys(integrationReferencePerType).includes(id)) {
          const DbClass = isClient ? ClientsDB : UsersDB;
          let db = new DbClass();
          let record = await db.read(id);
          if (isClient && record?.type === 'staff') {
            objectType = 'staff';
            db = new UsersDB();
            record = await db.read(this.userId);
          }
          objectId = record?.integrationReference;
        }

        if (!objectId) {
          throw new Error(`${objectType[0].toUpperCase()}${objectType.slice(1)} is not linked to an integration`);
        }

        await callFirebaseFunction('refreshAppointments', {
          clinicId: this.userDetails.clinic,
          objectType,
          objectId,
        }, {
          timeout: 540000, // timeout aligned with firebase function timeout
        });
        await this.loadAppointments();
      } catch (err) {
        const isOfflineError = ['internal', 'functions/internal'].includes(err.code)
          && err.message === 'internal'
          && !this.networkOnline;
        const errorMessage = isOfflineError ? this.$t('common.noInternetConnectionMessage') : err.message;
        notifyErrorWithDetails(errorMessage, err.details?.fullMessage);
      } finally {
        this.isRefreshingAppointments = false;
      }
    }, // onRefreshAppointments()

    async loadAppointments() {
      this.isRefreshingAppointments = true;
      const appointmentsDb = new AppointmentsDB();
      try {
        if ((this.clientId === null) && (this.userId === null)) {
          this.appointments = [];
          return;
        }

        // not sure if we always have this field in other integrations, so apply this only for Aloha
        const clientField = this.connectedClinic.integration === 'aloha' && this.clientId === null
          ? 'integrationClientParRef'
          : 'client';

        const query = [
          ['clinic', '==', this.userDetails.clinic],
          [clientField, '==', this.clientId],
          ['user', '==', this.userId],
          ['status', '==', 'scheduled'],
          ['scheduledStartTimestamp', '>=', this.startTimestamp],
          ['scheduledStartTimestamp', '<=', Timestamp.fromDate(
            this.adjustDate(this.startDate, { hour: 23, minute: 59, second: 59, millisecond: 999 }),
          )],
        ];

        const appointments = filter(
          await appointmentsDb.readAll(query),
          (appointment) => isEmpty(appointment.recurringWeekdays)
          && (appointment?.type !== 'unavailability')
          && (!includes(this.disableAppointments, appointment.id))
          && !(appointment?.cancellationRequest?.status === 'pending'),
        );
        if (this.appointmentId && (!find(appointments, { id: this.appointmentId }))) {
          const currentAppointment = await appointmentsDb.read(this.appointmentId);
          if (currentAppointment) {
            appointments.push(currentAppointment);
          }
        }
        // an appointment may have a session activity with a different user — filter those out.
        // assign to this.appointments only once, after all filtering, to avoid flickering.
        this.appointments = await this.filterAppointmentsWithCorrectUser(appointments);
      } finally {
        this.isRefreshingAppointments = false;
      }
    },

  },
};
</script>
<style scoped>
.appointments-dialog {
  min-width:80vw;
}
</style>
